import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LovApi } from '../../api/LovApi';
import { LovOptionsApi } from '../../api/LovOptionsApi';
import { updateTableData } from '../../store/utilities/SliceDataHelper';
import type {
  ILovRow,
  ILovFetchRequest,
  ILovUpdateRequest,
  ILovDeleteRequest,
  ILovOptionFetchAction,
  ILovOptionUpdateRequest,
  ILovOptionDeleteRequest,
} from '../../../types';
import type { IPaginatedState } from '../../../types/IComponentTypes';

/** Paginated state for the LOV list and the options of the currently opened LOV. */
const initialState = {
  lov: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  } as IPaginatedState<ILovRow>,
  lovOptions: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
  } as IPaginatedState<ILovRow>,
};

/** Fetches a page of LOVs, optionally filtered by a search term. */
export const getLov = createAsyncThunk('lov/getLov', async (action: ILovFetchRequest, thunkAPI) => {
  return LovApi.getLov(
    { page: action.page, limit: action.limit, search: action.searchCode },
    thunkAPI.dispatch
  );
});

/** Updates a single LOV. The caller reloads the current page on success. */
export const updateLov = createAsyncThunk(
  'lov/updateLov',
  async (action: ILovUpdateRequest, thunkAPI) => {
    return LovApi.updateLov(action, thunkAPI.dispatch);
  }
);

/** Deletes the given LOVs. The caller reloads the current page on success. */
export const deleteLov = createAsyncThunk(
  'lov/deleteLov',
  async (action: ILovDeleteRequest, thunkAPI) => {
    return LovApi.deleteLov(action, thunkAPI.dispatch);
  }
);

/** Fetches a page of options belonging to one LOV, optionally filtered by a search term. */
export const getLovOption = createAsyncThunk(
  'lov/getLovOption',
  async (action: ILovOptionFetchAction, thunkAPI) => {
    return LovOptionsApi.getLovOption(
      {
        lovCode: action.lovCode,
        page: action.page,
        limit: action.limit,
        search: action.searchCode,
      },
      thunkAPI.dispatch
    );
  }
);

/** Updates a single option of a LOV. The caller reloads the current page on success. */
export const updateLovOption = createAsyncThunk(
  'lov/updateLovOption',
  async (action: ILovOptionUpdateRequest, thunkAPI) => {
    return LovOptionsApi.updateLovOption(action, thunkAPI.dispatch);
  }
);

/** Deletes the given options of a LOV. The caller reloads the list on success. */
export const deleteLovOption = createAsyncThunk(
  'lov/deleteLovOption',
  async (action: ILovOptionDeleteRequest, thunkAPI) => {
    return LovOptionsApi.deleteLovOption(action, thunkAPI.dispatch);
  }
);

/**
 * Redux slice for LOV maintenance, registered as `lovManagement`.
 *
 * Only the fetch thunks update state; create, update and delete are handled by the
 * components, which re-fetch the affected page afterwards.
 */
const lovSlice = createSlice({
  name: 'lov',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLov.fulfilled, (state, action) => {
        if (action.payload && !('error' in action.payload)) {
          updateTableData(state.lov, action.payload);
        }
      })
      .addCase(getLovOption.fulfilled, (state, action) => {
        if (action.payload && !('error' in action.payload)) {
          updateTableData(
            state.lovOptions,
            action.payload as Parameters<typeof updateTableData>[1]
          );
        }
      });
  },
});

export default lovSlice.reducer;
